import { createAction } from '@reduxjs/toolkit';
import * as ActionTypes from './types';

export interface UserApplicationUserBody {
    accessTokenFromAuthentication: {
        accessToken: string;
        account: {
            accountIdentifier: string;
            environment: string;
            homeAccountIdentifier: string;
            idToken: {
                aud: string;
                auth_time: number;
                exp: number;
                iat: number;
                idp: number;
                iss: string;
                nbf: number;
                nonce: string;
                sub: string;
                tfp: string;
                ver: string;
            };
            idTokenClaims: {
                aud: string;
                auth_time: number;
                exp: number;
                iat: number;
                idp: number;
                iss: string;
                nbf: number;
                nonce: string;
                sub: string;
                tfp: string;
                ver: string;
            };
            name: undefined | string;
            sid: undefined | string | number;
            userName: undefined | string | number;

            accountState: string;
            expiresOn: any | {};
            fromCache: boolean;
            scopes: Array<string>;
            tenantId: string;
            tokenType: string;
            uniqueId: string;
        };
    };
}
export interface ApplicationUserPayload {
    accessTokenFromAuthentication: {
        accessToken: string;
    };
}
export interface ApplicationUsers {
    displayName: string;
    email: string;
    firstName: string;
    fullName: string;
    groups: Array<string>;
    id: string;
    isClaimant: boolean;
    isSystem: boolean;
    lastName: string;
    areTermsConsented: boolean;
    wordings: any;
    languageId: string;
}

export interface GeneralResponse {
    data: ApplicationUsers;
}

export const login = createAction<loginAction>(ActionTypes.LOGIN);
export const loginSucceeded = createAction<loginSucceededAction>(
    ActionTypes.LOGIN_SUCCEEDED,
);
export const loginFailed = createAction<loginFailedAction>(
    ActionTypes.LOGIN_FAILED,
);
export const updateToken = createAction<updateTokenAction>(
    ActionTypes.UPDATE_TOKEN,
);

export const fetchApplicationUserDetails =
    createAction<fetchApplicationUserDetailsAction>(
        ActionTypes.FETCH_APPLICATION_USER_DETAILS,
    );
export const fetchApplicationUserDetailsSuccess =
    createAction<fetchApplicationUserDetailsSuccessAction>(
        ActionTypes.FETCH_APPLICATION_USER_DETAILS_SUCCESS,
    );
export const fetchApplicationUserDetailsFailed =
    createAction<fetchApplicationUserDetailsFailedAction>(
        ActionTypes.FETCH_APPLICATION_USER_DETAILS_FAILED,
    );

export const logout = createAction<logoutAction>(ActionTypes.LOGOUT);
export const logoutSuccess = createAction<logoutSuccessAction>(
    ActionTypes.LOGOUT_SUCCESS,
);
export const logoutFailed = createAction<logoutFailedAction>(
    ActionTypes.LOGOUT_FAILED,
);

export const fetchApplicationCustomerFeedback =
    createAction<fetchApplicationCustomerFeedbackAction>(
        ActionTypes.FETCH_APPLICATION_CUSTOMER_FEEDBACK,
    );
export const fetchApplicationCustomerFeedbackSuccess =
    createAction<fetchApplicationCustomerFeedbackSuccessAction>(
        ActionTypes.FETCH_APPLICATION_CUSTOMER_FEEDBACK_SUCCESS,
    );
export const fetchApplicationCustomerFeedbackFailed =
    createAction<fetchApplicationCustomerFeedbackFailedAction>(
        ActionTypes.FETCH_APPLICATION_CUSTOMER_FEEDBACK_FAILED,
    );

export const updateApplicationTermsAndConditionsTermsConsented =
    createAction<updateApplicationTermsAndConditionsTermsConsentedAction>(
        ActionTypes.UPDATE_APPLICATION_TERMS_CONSENTED,
    );
export const updateApplicationTermsAndConditionsTermsConsentedSuccess =
    createAction<updateApplicationTermsAndConditionsTermsConsentedSuccessAction>(
        ActionTypes.UPDATE_APPLICATION_TERMS_CONSENTED_SUCCESS,
    );
export const updateApplicationTermsAndConditionsTermsConsentedFailed =
    createAction<updateApplicationTermsAndConditionsTermsConsentedFailedAction>(
        ActionTypes.UPDATE_APPLICATION_TERMS_CONSENTED_FAILED,
    );

export const fetchApplicationUserWordings = createAction<
    fetchApplicationUserWordingsAction | undefined
>(ActionTypes.FETCH_APPLICATION_USER_WORDINGS);
export const fetchApplicationUserWordingsSuccess =
    createAction<fetchApplicationUserWordingsSuccessAction>(
        ActionTypes.FETCH_APPLICATION_USER_WORDINGS_SUCCESS,
    );
export const fetchApplicationUserWordingsFailed =
    createAction<fetchApplicationUserWordingsFailedAction>(
        ActionTypes.FETCH_APPLICATION_USER_WORDINGS_FAILED,
    );
export const fetchUnauthorisedUserWordings = createAction<
    fetchUnauthorisedWordingsAction | undefined
>(ActionTypes.FETCH_UNAUTHORISED_WORDINGS);
export const fetchUnauthorisedUserWordingsSuccess =
    createAction<fetchUnauthorisedWordingsSuccessAction>(
        ActionTypes.FETCH_UNAUTHORISED_WORDINGS_SUCCESS,
    );
export const fetchUnauthorisedUserWordingsFailed =
    createAction<fetchUnauthorisedWordingsFailedAction>(
        ActionTypes.FETCH_UNAUTHORISED_WORDINGS_FAILED,
    );
export const addResourceToi18n = createAction<addResourceToi18nAction>(
    ActionTypes.ADD_RESOURCE_FILE_TO_I18N,
);
export const addResourceToi18nSuccess =
    createAction<addResourceToi18nSuccessAction>(
        ActionTypes.ADD_RESOURCE_FILE_TO_I18N_SUCCESS,
    );

export const updateLanguageApplicationUser =
    createAction<updateLanguageApplicationUserAction>(
        ActionTypes.UPDATE_LANGUAGE_APPLICATION_USER,
    );
export const updateLanguageApplicationUserSuccess =
    createAction<updateLanguageApplicationUserSuccessAction>(
        ActionTypes.UPDATE_LANGUAGE_APPLICATION_USER_SUCCESS,
    );
export const updateLanguageApplicationUserFailed =
    createAction<updateLanguageApplicationUserFailedAction>(
        ActionTypes.UPDATE_LANGUAGE_APPLICATION_USER_FAILED,
    );

export interface updateLanguageApplicationUserAction {
    type: typeof ActionTypes.UPDATE_LANGUAGE_APPLICATION_USER;
}

export interface updateLanguageApplicationUserSuccessAction {
    type: typeof ActionTypes.UPDATE_LANGUAGE_APPLICATION_USER_SUCCESS;
}

export interface updateLanguageApplicationUserFailedAction {
    type: typeof ActionTypes.UPDATE_LANGUAGE_APPLICATION_USER_FAILED;
}

export interface loginAction {
    type: typeof ActionTypes.LOGIN;
    payload: unknown;
}

export interface loginSucceededAction {
    type: typeof ActionTypes.LOGIN_SUCCEEDED;
    payload: UserApplicationUserBody;
}

export interface loginFailedAction {
    type: typeof ActionTypes.LOGIN_FAILED;
    payload: UserApplicationUserBody;
}

export interface updateTokenAction {
    type: typeof ActionTypes.UPDATE_TOKEN;
    payload: ApplicationUserPayload;
}

export interface fetchApplicationUserDetailsAction {
    type: typeof ActionTypes.FETCH_APPLICATION_USER_DETAILS;
    payload: undefined;
}

export interface fetchApplicationUserDetailsSuccessAction {
    type: typeof ActionTypes.FETCH_APPLICATION_USER_DETAILS_SUCCESS;
    payload: GeneralResponse;
    body: GeneralResponse;
}

export interface fetchApplicationUserDetailsFailedAction {
    type: typeof ActionTypes.FETCH_APPLICATION_USER_DETAILS_FAILED;
    payload: GeneralResponse;
}

export interface logoutAction {
    type: typeof ActionTypes.LOGOUT;
    payload: undefined;
}

export interface logoutSuccessAction {
    type: typeof ActionTypes.LOGOUT_SUCCESS;
    payload: unknown;
}

export interface logoutFailedAction {
    type: typeof ActionTypes.LOGOUT_FAILED;
    payload: unknown;
}
export interface fetchApplicationCustomerFeedbackAction {
    type?: typeof ActionTypes.FETCH_APPLICATION_CUSTOMER_FEEDBACK;
}
export interface fetchApplicationCustomerFeedbackSuccessAction {
    body: { data: boolean };
    type: typeof ActionTypes.FETCH_APPLICATION_CUSTOMER_FEEDBACK_SUCCESS;
    payload: unknown;
}
export interface fetchApplicationCustomerFeedbackFailedAction {
    type: typeof ActionTypes.FETCH_APPLICATION_CUSTOMER_FEEDBACK_FAILED;
    payload: unknown;
}
export interface fetchApplicationUserWordingsAction {
    type?: typeof ActionTypes.FETCH_APPLICATION_USER_WORDINGS;
    payload: undefined;
}
export interface fetchApplicationUserWordingsSuccessAction {
    payload: GeneralResponse;
    body: GeneralResponse;
    type: typeof ActionTypes.FETCH_APPLICATION_USER_WORDINGS_SUCCESS;
}
export interface fetchApplicationUserWordingsFailedAction {
    type: typeof ActionTypes.FETCH_APPLICATION_USER_WORDINGS_FAILED;
}
export interface fetchUnauthorisedWordingsAction {
    type?: typeof ActionTypes.FETCH_UNAUTHORISED_WORDINGS;
    payload: {
        languageId: string;
        claimApplicationId?: string;
    };
}
export interface fetchUnauthorisedWordingsSuccessAction {
    payload: GeneralResponse;
    body: GeneralResponse;
    type: typeof ActionTypes.FETCH_UNAUTHORISED_WORDINGS_SUCCESS;
}
export interface fetchUnauthorisedWordingsFailedAction {
    type: typeof ActionTypes.FETCH_UNAUTHORISED_WORDINGS_FAILED;
}
export interface addResourceToi18nAction {
    type?: typeof ActionTypes.ADD_RESOURCE_FILE_TO_I18N;
}

export interface addResourceToi18nSuccessAction {
    type?: typeof ActionTypes.ADD_RESOURCE_FILE_TO_I18N_SUCCESS;
}

export interface updateApplicationTermsAndConditionsTermsConsentedAction {
    type?: typeof ActionTypes.UPDATE_APPLICATION_TERMS_CONSENTED;
    payload: any;
}
export interface updateApplicationTermsAndConditionsTermsConsentedSuccessAction {
    body: { data: boolean };
    type: typeof ActionTypes.UPDATE_APPLICATION_TERMS_CONSENTED_SUCCESS;
    payload: unknown;
}
export interface updateApplicationTermsAndConditionsTermsConsentedFailedAction {
    type: typeof ActionTypes.UPDATE_APPLICATION_TERMS_CONSENTED_FAILED;
    payload: unknown;
}
